/**
 * Build the locale → messages map from eager-loaded modules.
 * @param {Record<string, any>} modules
 * @returns {Record<string, any>}
 */
function isPlainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value)
}

function deepMerge(target, source) {
  Object.keys(source).forEach((key) => {
    const sourceValue = source[key]
    const targetValue = target[key]

    if (isPlainObject(sourceValue)) {
      if (!isPlainObject(targetValue)) {
        target[key] = {}
      }

      deepMerge(target[key], sourceValue)
    } else {
      target[key] = sourceValue
    }
  })

  return target
}

const LOCALE_KEY_PATTERN = /^[a-z]{2}(?:[-_][A-Za-z0-9]+)?$/

export function buildMessages(modules = {}) {
  return Object.entries(modules).reduce((acc, [path, moduleExport]) => {
    const candidate = moduleExport?.default ?? moduleExport

    if (!candidate || typeof candidate !== 'object') {
      throw new Error(`Locale module ${path} did not export an object`)
    }

    const localeEntries = Object.entries(candidate)

    if (localeEntries.length === 0) {
      throw new Error(`Locale module ${path} missing locale root`)
    }

    const hasLocale = localeEntries.some(([locale]) =>
      LOCALE_KEY_PATTERN.test(locale)
    )

    if (!hasLocale) {
      throw new Error(`Locale module ${path} missing locale root`)
    }

    localeEntries.forEach(([locale, messages]) => {
      if (!LOCALE_KEY_PATTERN.test(locale)) {
        return
      }
      if (!messages || typeof messages !== 'object') {
        throw new Error(
          `Locale module ${path} has invalid payload for ${locale}`
        )
      }

      if (!acc[locale]) {
        acc[locale] = {}
      }

      deepMerge(acc[locale], messages)
    })

    return acc
  }, {})
}

export default function loadMessages() {
  const modules = import.meta.glob('../../../config/locales/**/*.{yml,yaml}', {
    eager: true
  })

  return buildMessages(modules)
}
